import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, ChevronDown } from 'lucide-react';
import { createSectionTracker, trackVideoPlay, trackVideoProgress } from '../../services/funnelTracker';

interface VSLSectionProps {
  videoSrc: string;
}

export default function VSLSection({ videoSrc }: VSLSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const progressRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const tracker = createSectionTracker('vsl');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  useEffect(() => {
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  const handlePlayClick = () => {
    setIsPlaying(true);
    trackVideoPlay();

    setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      video.play();

      progressRef.current = setInterval(() => {
        if (video.duration > 0) {
          const percent = (video.currentTime / video.duration) * 100;
          trackVideoProgress(percent);
        }
      }, 3000);

      video.addEventListener('ended', () => {
        trackVideoProgress(100);
        if (progressRef.current) clearInterval(progressRef.current);
      }, { once: true });
    }, 100);
  };

  return (
    <section ref={sectionRef} id="vsl-section" className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
              De radiólogo a Data Engineer{' '}
              <span className="text-green-400">remoto</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Sin título en sistemas. Con garra.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden border-2 border-green-500/30 shadow-2xl shadow-green-500/10 max-w-sm mx-auto">
            {isPlaying ? (
              <video
                ref={videoRef}
                src={videoSrc}
                controls
                playsInline
                className="w-full bg-black"
              />
            ) : (
              <div
                className="relative aspect-[9/16] bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center cursor-pointer group"
                onClick={handlePlayClick}
              >
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-purple-500/20" />
                </div>

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 bg-green-600 hover:bg-green-500 rounded-full flex items-center justify-center shadow-2xl shadow-green-600/30 transition-colors"
                >
                  <Play className="w-10 h-10 sm:w-12 sm:h-12 text-white ml-1" />
                </motion.div>

                <div className="absolute bottom-6 sm:bottom-8 left-0 right-0 text-center">
                  <p className="text-white font-semibold text-base sm:text-lg">
                    Mirá mi historia en 1 minuto
                  </p>
                </div>
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-gray-400 text-base sm:text-lg mb-3">
              ¿Te gustaría hacer lo mismo? Seguí scrolleando.
            </p>
            <ChevronDown className="w-6 h-6 text-green-400 mx-auto animate-bounce" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
